/* =============================================================================
   Sanitizer - the HTML policy of the library: SAFE BY DEFAULT.
   Every user- or data-derived string a component renders goes through escape()
   or setContent(); raw HTML is an EXPLICIT, greppable opt-in through
   Sanitizer.html(...), which marks the string as reviewed by wrapping it in
   TrustedHtml. Anything that is not a TrustedHtml is escaped, so forgetting to
   opt in fails safe (visible markup) rather than unsafe (injected markup).
   TWO TIERS, because not every field may be flattened to text:
     escape / setContent  the DEFAULT - renders the value as TEXT.
     sanitizeHtml(...)    for a field that must KEEP markup (a tree node caption
                          renders anchors as links). Allowlisted formatting tags
                          only, a per-tag attribute allowlist - so every on*
                          handler dies - and safe urls only, never
                          javascript: / data: / vbscript:.
   The policy applies uniformly to window titles, message-box text, tooltip
   content, tree node captions and anything pushed in from the server.
   API: escape · html · render · setContent · sanitizeHtml · TrustedHtml (also
   grouped on the Sanitizer object). setContent uses textContent for plain strings.
   ============================================================================= */

const MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** escape text for safe interpolation into HTML (the DEFAULT path) */
export function escape(text) {
  return String(text ?? '').replace(/[&<>"']/g, (c) => MAP[c]);
}

/** Marker wrapper for reviewed HTML - the EXPLICIT opt-in, never applied implicitly. */
export class TrustedHtml {
  constructor(html) { this.html = String(html); }
  toString() { return this.html; }
}

/** declare a string as reviewed/trusted HTML (grep-able opt-in: Sanitizer.html(...)) */
export function html(trusted) { return new TrustedHtml(trusted); }

/** render a value: TrustedHtml passes through, everything else is escaped */
export function render(value) {
  return value instanceof TrustedHtml ? value.html : escape(value);
}

/** set element content under the policy (textContent fast-path for plain strings) */
export function setContent(el, value) {
  if (value instanceof TrustedHtml) el.innerHTML = value.html;
  else el.textContent = String(value ?? '');
  return el;
}

/* ---- the ALLOWLIST tier ---------------------------------------------------------
   ONE implementation serves every consumer: the richtext editor re-exports it
   (`Ics.richtext.sanitize`) and ics-tree.js renders node captions through it.
   RULE: the list of surfaces this header claims to cover and the code must stay
   together - a consumer that sets innerHTML raw is outside the policy however the
   header reads, so a new consumer imports this function rather than copying it or
   bypassing it. */
const ALLOWED = new Set(['P', 'BR', 'DIV', 'SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL',
  'OL', 'UL', 'LI', 'A', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'CODE', 'SUB', 'SUP', 'HR',
  'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TD', 'TH', 'IMG', 'FONT']);
const DROP_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FRAME', 'FRAMESET', 'LINK', 'META', 'TEMPLATE', 'NOSCRIPT', 'FORM', 'INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'SVG', 'MATH', 'HEAD', 'TITLE', 'BASE']);
const ATTR_ALLOWED = { A: ['href', 'title', 'target'], IMG: ['src', 'alt', 'width', 'height'], TD: ['colspan', 'rowspan'], TH: ['colspan', 'rowspan'], FONT: ['color', 'face', 'size'] };
const SAFE_URL = /^(https?:|mailto:|tel:|\/|#|[^:]*$)/i;    // absolute http(s)/mailto/tel, root/relative, fragment; NEVER javascript:/data:/vbscript:

/** the one HTML policy: allowlisted formatting only, safe urls, no scripts/handlers/styles.
    opts.allowData = true additionally keeps INERT `data-*` attributes. Opt-in, because
    the app menu's node captions carry `<a href="#x" data-url="/COA/View">` and the app
    shell navigates by reading `a[data-url]` - stripping it would silently kill menu
    navigation on every page, so the menu tree passes allowData and a plain caption
    field does not.
    `data-ics*` is NEVER kept even then: it is the library's own mount attribute, so
    honouring it from data would be behaviour injection rather than markup. */
export function sanitizeHtml(html, opts) {
  const allowData = !!(opts && opts.allowData);
  const src = String(html == null ? '' : html);
  if (!src.trim()) return '';
  const doc = new DOMParser().parseFromString('<body>' + src + '</body>', 'text/html');
  const walk = (node) => {
    const kids = Array.from(node.childNodes);
    for (const k of kids) {
      if (k.nodeType === 8) { k.remove(); continue; }                       // comments
      if (k.nodeType !== 1) continue;                                       // text stays
      const tag = k.tagName;
      if (DROP_WITH_CONTENT.has(tag)) { k.remove(); continue; }
      if (!ALLOWED.has(tag)) {                                              // unknown wrapper: keep its content, drop the tag
        while (k.firstChild) node.insertBefore(k.firstChild, k);
        k.remove();
        continue;
      }
      for (const a of Array.from(k.attributes)) {
        const n = a.name.toLowerCase();
        let ok = (ATTR_ALLOWED[tag] || []).includes(n);
        if (!ok && allowData && /^data-(?!ics)[a-z0-9_.:-]+$/.test(n)) ok = true;   // inert payload, never the mount attribute
        if (!ok) { k.removeAttribute(a.name); continue; }
        if ((n === 'href' || n === 'src') && !SAFE_URL.test(a.value.trim())) k.removeAttribute(a.name);
        if (n === 'target' && a.value !== '_blank') k.removeAttribute(a.name);
      }
      if (tag === 'A' && k.getAttribute('target') === '_blank') k.setAttribute('rel', 'noopener noreferrer');
      walk(k);
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

export const Sanitizer = { escape, html, render, setContent, sanitizeHtml, TrustedHtml };
